import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaArrowRight, FaCheckCircle, FaUser, FaBuilding, FaQuestionCircle } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { getMyAccountSnapshot } from '../services/firestoreService';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import StartSidebar from '../components/StartSidebar';

const ProgressBar = ({ progress }) => {
  const { darkMode } = useTheme();
  
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Progress</span>
        <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{Math.round(progress)}% Complete</span>
      </div>
      <div className={`w-full rounded-full h-3 relative overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
        <div 
          className="bg-gradient-to-r from-red-500 via-red-600 to-red-700 h-3 rounded-full transition-all duration-700 ease-out relative"
          style={{ width: `${progress}%` }}
        >
          {/* Animated moving lines */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
          <div 
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            style={{
              animation: 'shimmer 2s infinite',
              backgroundSize: '200% 100%',
              backgroundPosition: 'left'
            }}
          ></div>
        </div>
        {/* Glowing effect */}
        <div 
          className="absolute top-0 h-3 rounded-full transition-all duration-700 ease-out"
          style={{ 
            width: `${progress}%`,
            background: 'linear-gradient(90deg, rgba(239,68,68,0.3) 0%, rgba(220,38,38,0.3) 100%)',
            filter: 'blur(8px)'
          }}
        ></div>
      </div>
    </div>
  );
};

const Discovery = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    companyName: '',
    phoneNumber: '',
    timezone: '',
    // Address Information
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    // Business Information
    businessStructure: '',
    businessStructureOther: '',
    dba: '',
    website: '',
    yearsInBusiness: '',
    // Background Questions
    background: '',
    whyStarted: '',
    marketInfo: ''
  });

  const [progress, setProgress] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [isPrepopulated, setIsPrepopulated] = useState(false);

  const sections = [
    {
      id: 'personal',
      title: 'Personal Information',
      icon: FaUser
    },
    {
      id: 'business',
      title: 'Business Information',
      icon: FaBuilding
    },
    {
      id: 'background',
      title: 'Background Questions',
      icon: FaQuestionCircle
    }
  ];

  const timezoneOptions = [
    'Eastern Time (ET)',
    'Central Time (CT)',
    'Mountain Time (MT)',
    'Pacific Time (PT)',
    'Alaska Time (AKT)',
    'Hawaii Time (HST)',
    'Atlantic Time (AT)',
    'Other'
  ];

  const businessStructureOptions = [
    'Sole Proprietorship',
    'Partnership',
    'LLC',
    'S Corporation',
    'C Corporation',
    'Other'
  ];

  const stateOptions = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana',
    'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
    'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska',
    'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina',
    'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
    'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  // Calculate progress based on form completion
  useEffect(() => {
    let completedFields = 0;
    let totalFields = 0;

    // Personal Information fields
    if (formData.firstName) completedFields++;
    if (formData.lastName) completedFields++;
    if (formData.companyName) completedFields++;
    if (formData.phoneNumber) completedFields++;
    if (formData.timezone) completedFields++;
    if (formData.streetAddress) completedFields++;
    if (formData.city) completedFields++;
    if (formData.state) completedFields++;
    if (formData.zipCode) completedFields++;
    totalFields += 9;

    // Business Information fields
    if (formData.businessStructure) completedFields++;
    if (formData.businessStructure === 'Other' && formData.businessStructureOther) completedFields++;
    if (formData.dba) completedFields++;
    if (formData.website) completedFields++;
    if (formData.yearsInBusiness) completedFields++;
    totalFields += 5;

    // Background Questions fields
    if (formData.background) completedFields++;
    if (formData.whyStarted) completedFields++;
    if (formData.marketInfo) completedFields++;
    totalFields += 3;

    const calculatedProgress = (completedFields / totalFields) * 100;
    setProgress(calculatedProgress);
  }, [formData]);

  // Load existing data when component mounts
  useEffect(() => {
    const loadExistingData = async () => {
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // First, try to load existing discovery data
            if (userData.discovery) {
              setFormData(prev => ({
                ...prev,
                ...userData.discovery
              }));
            } else {
              // If no discovery data exists, prepopulate from profile data
              const profileData = {
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                companyName: userData.company || '',
                phoneNumber: userData.phone || '',
                timezone: userData.timezone || '',
                streetAddress: userData.address?.street || '',
                city: userData.address?.city || '',
                state: userData.address?.state || '',
                zipCode: userData.address?.zipCode || '',
                country: userData.address?.country || 'United States'
              };
              
              // Only prepopulate if we have meaningful data (not just empty strings or single characters)
              const hasValidData = Object.values(profileData).some(value => 
                value && value.toString().trim().length > 1 && value !== 'd' && value !== 'D'
              );
              
              if (hasValidData) {
                console.log('Prepopulating Discovery form with profile data:', profileData);
                setFormData(prev => ({
                  ...prev,
                  ...profileData
                }));
                setIsPrepopulated(true);
              }
            }
          }
        } catch (error) {
          console.error('Error loading discovery data:', error);
        }
      }
    };

    loadExistingData();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      console.log('=== SAVING DISCOVERY DATA ===');
      console.log('Form data:', formData);
      
      if (currentUser) {
        // Get existing user data to preserve completed steps
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        let existingCompletedSteps = [];
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.startFlowProgress && userData.startFlowProgress.completedSteps) {
            existingCompletedSteps = userData.startFlowProgress.completedSteps;
          }
        }
        
        // Add step 2 if not already completed
        if (!existingCompletedSteps.includes(2)) {
          existingCompletedSteps.push(2);
        }
        
        // Save discovery data to user document
        await updateDoc(doc(db, 'users', currentUser.uid), {
          discovery: formData,
          darkMode: darkMode, // Save theme preference
          startFlowProgress: {
            completedSteps: existingCompletedSteps,
            lastUpdated: new Date().toISOString()
          },
          lastUpdated: new Date().toISOString()
        });
        
        console.log('Discovery data saved to user document');
        
        // Try to find and update the lead account with discovery data
        try {
          // The lead account this login is linked to (clients may only read their own)
          const accountsSnapshot = await getMyAccountSnapshot('Leads');
          
          if (!accountsSnapshot.empty) {
            const leadDoc = accountsSnapshot.docs[0];
            const leadData = leadDoc.data();
            
            console.log('Found lead account:', leadDoc.id, 'Updating with discovery data');
            
            // Prepare discovery data for lead account
            const discoveryData = {
              // Personal Information
              firstName: formData.firstName,
              lastName: formData.lastName,
              companyName: formData.companyName,
              phoneNumber: formData.phoneNumber,
              timezone: formData.timezone,
              
              // Address Information
              streetAddress: formData.streetAddress,
              city: formData.city,
              state: formData.state,
              zipCode: formData.zipCode,
              country: formData.country,
              
              // Website
              website: formData.website,
              
              // Business Information for assessment
              businessStructure: formData.businessStructure,
              businessStructureOther: formData.businessStructureOther,
              dba: formData.dba,
              yearsInBusiness: formData.yearsInBusiness,
              
              // Background Questions for assessment
              background: formData.background,
              whyStarted: formData.whyStarted,
              marketInfo: formData.marketInfo
            };
            
            // Clean the discovery data to remove undefined values
            const cleanDiscoveryData = Object.fromEntries(
              Object.entries(discoveryData).filter(([key, value]) => value !== undefined && value !== '')
            );
            
            console.log('Updating lead account with clean discovery data:', cleanDiscoveryData);
            console.log('Lead account ID:', leadDoc.id);
            console.log('Lead account email:', leadData.email);
            
            // Update the lead account with discovery information
            const updateData = {
              discovery: cleanDiscoveryData,
              discoveryCompleted: true,
              discoveryCompletedAt: new Date().toISOString(),
              lastUpdated: new Date().toISOString()
            };
            
            console.log('Update data structure:', updateData);
            
            await updateDoc(doc(db, 'accounts', leadDoc.id), updateData);
            
            console.log('Discovery data successfully saved to lead account:', leadDoc.id);
            console.log('Discovery data structure saved:', cleanDiscoveryData);
          } else {
            console.log('No lead account found for user:', currentUser.email);
            console.log('Discovery data saved to user document only. Lead account will be updated when created.');
          }
        } catch (leadUpdateError) {
          console.error('Error updating lead account with discovery data:', leadUpdateError);
          console.error('Lead update error details:', {
            error: leadUpdateError.message,
            code: leadUpdateError.code,
            userEmail: currentUser.email
          });
          // Continue even if lead update fails
        }
      }

      // Navigate back to start dashboard
      navigate('/start/dashboard');
    } catch (error) {
      console.error('Error saving discovery data:', error);
      // In dev mode, just continue even if save fails
      if (process.env.NODE_ENV === 'development') {
        navigate('/start/dashboard');
      }
    }
  };

  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const renderPersonalInformation = () => (
    <div className="space-y-8">
      
      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            First Name *
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
                : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
            }`}
            placeholder="Enter your first name"
            required
          />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Last Name *
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
                : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
            }`}
            placeholder="Enter your last name"
            required
          />
        </div>
      </div>

      {/* Company Name */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Company Name *
        </label>
        <input
          type="text"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
            darkMode 
              ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
              : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
          }`}
          placeholder="Enter your company name"
          required
        />
      </div>

      {/* Phone Number and Timezone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Phone Number *
          </label>
          <input
            type="tel"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
                : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
            }`}
            placeholder="(555) 123-4567"
            required
          />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Timezone *
          </label>
          <select
            name="timezone"
            value={formData.timezone}
            onChange={handleChange}
            className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
                : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
            }`}
            required
          >
            <option value="">Select your timezone</option>
            {timezoneOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Address Information */}
      <div className="p-6 rounded-lg border-2 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700">
        <h4 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Address Information
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Street Address *
            </label>
            <input
              type="text"
              name="streetAddress"
              value={formData.streetAddress}
              onChange={handleChange}
              className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
                darkMode 
                  ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
              }`}
              placeholder="Enter your street address"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
                  darkMode 
                    ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
                }`}
                placeholder="Enter your city"
                required
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                State *
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
                  darkMode 
                    ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
                }`}
                required
              >
                <option value="">Select your state</option>
                {stateOptions.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                ZIP Code *
              </label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
                  darkMode 
                    ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
                }`}
                placeholder="Enter your ZIP code"
                required
              />
            </div>
          </div>
        </div>
      </div>

      {/* Theme Preference */}
      <div className="p-6 rounded-lg border-2 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-700">
        <h4 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Theme Preference
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto mb-4">
          <button
            type="button"
            onClick={() => toggleDarkMode()}
            className={`p-4 rounded-lg border-2 transition-all duration-200 ${
              !darkMode
                ? 'border-purple-500 bg-purple-100 dark:bg-purple-900/30'
                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400'
            }`}
          >
            <span className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              ☀️ Light Mode
            </span>
          </button>
          <button
            type="button"
            onClick={() => toggleDarkMode()}
            className={`p-4 rounded-lg border-2 transition-all duration-200 ${
              darkMode
                ? 'border-purple-500 bg-purple-100 dark:bg-purple-900/30'
                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400'
            }`}
          >
            <span className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              🌙 Dark Mode
            </span>
          </button>
        </div>
        
        <p className={`text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          You can change this anytime in your Profile Settings
        </p>
      </div>
    </div>
  );

  const renderBusinessInformation = () => (
    <div className="space-y-8">
      
      {/* Business Structure */}
      <div className="p-6 rounded-lg border-2 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700">
        <h4 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Business Structure *
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-6">
          {businessStructureOptions.map(option => (
            <button
              key={option}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, businessStructure: option }))}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                formData.businessStructure === option
                  ? 'border-green-500 bg-green-100 dark:bg-green-900/30'
                  : 'border-gray-300 dark:border-gray-600 hover:border-green-400'
              }`}
            >
              <span className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{option}</span>
            </button>
          ))}
        </div>

        {/* Other input for business structure */}
        {formData.businessStructure === 'Other' && (
          <div className="max-w-md mx-auto">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Please specify your business structure:
            </label>
            <input
              type="text"
              name="businessStructureOther"
              value={formData.businessStructureOther || ''}
              onChange={handleChange}
              className={`w-full p-3 border-2 rounded-lg ${
                darkMode 
                  ? 'bg-gray-800 border-gray-600 text-white focus:border-green-400' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-green-400'
              }`}
              placeholder="Enter your business structure..."
              required
            />
          </div>
        )}
      </div>

      {/* DBA */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          DBA (Doing Business As)
        </label>
        <input
          type="text"
          name="dba"
          value={formData.dba}
          onChange={handleChange}
          className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
            darkMode 
              ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
              : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
          }`}
          placeholder="Enter your DBA if applicable"
        />
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Leave blank if you don't have a DBA
        </p>
      </div>

      {/* Website */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Website
        </label>
        <input
          type="url"
          name="website"
          value={formData.website}
          onChange={handleChange}
          className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
            darkMode 
              ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
              : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
          }`}
          placeholder="https://www.yourcompany.com"
        />
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Include https:// if you have a website
        </p>
      </div>

      {/* Years in Business */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Years in Business *
        </label>
        <input
          type="number"
          name="yearsInBusiness"
          value={formData.yearsInBusiness}
          onChange={handleChange}
          min="0"
          max="100"
          className={`w-full p-3 border-2 rounded-lg transition-all duration-200 ${
            darkMode 
              ? 'bg-gray-800 border-gray-600 text-white focus:border-indigo-400' 
              : 'bg-white border-gray-300 text-gray-900 focus:border-indigo-400'
          }`}
          placeholder="Enter number of years"
          required
        />
      </div>
    </div>
  );

  const renderBackgroundQuestions = () => (
    <div className="space-y-8">
      
      {/* Background Question */}
      <div className="p-6 rounded-lg border-2 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-yellow-200 dark:border-yellow-700">
        <h4 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          What is your background and how did you start working in construction? *
        </h4>
        
        <textarea
          name="background"
          value={formData.background}
          onChange={handleChange}
          rows={6}
          className={`w-full p-3 border-2 rounded-lg resize-none transition-all duration-200 ${
            darkMode 
              ? 'bg-gray-800 border-gray-600 text-white focus:border-yellow-400' 
              : 'bg-white border-gray-300 text-gray-900 focus:border-yellow-400'
          }`}
          placeholder="Tell us about your journey into construction, your experience, training, and what led you to this industry..."
          required
        />
      </div>

      {/* Why Started Question */}
      <div className="p-6 rounded-lg border-2 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700">
        <h4 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Why did you start your company? *
        </h4>
        
        <textarea
          name="whyStarted"
          value={formData.whyStarted}
          onChange={handleChange}
          rows={6}
          className={`w-full p-3 border-2 rounded-lg resize-none transition-all duration-200 ${
            darkMode 
              ? 'bg-gray-800 border-gray-600 text-white focus:border-blue-400' 
              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-400'
          }`}
          placeholder="What motivated you to start your own company? What was your vision and what problems were you trying to solve?"
          required
        />
      </div>

      {/* Market Info Question */}
      <div className="p-6 rounded-lg border-2 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700">
        <h4 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Please tell us about your market, don't forget to include the size of your market! *
        </h4>
        
        <textarea
          name="marketInfo"
          value={formData.marketInfo}
          onChange={handleChange}
          rows={6}
          className={`w-full p-3 border-2 rounded-lg resize-none transition-all duration-200 ${
            darkMode 
              ? 'bg-gray-800 border-gray-600 text-white focus:border-green-400' 
              : 'bg-white border-gray-300 text-gray-900 focus:border-green-400'
          }`}
          placeholder="Describe your target market, geographic area, customer demographics, market size, competition, and any other relevant market information..."
          required
        />
      </div>
    </div>
  );

  const renderCurrentSection = () => {
    switch (currentSection) {
      case 0:
        return renderPersonalInformation();
      case 1:
        return renderBusinessInformation();
      case 2:
        return renderBackgroundQuestions();
      default:
        return renderPersonalInformation();
    }
  };

  return (
    <div className={`min-h-screen`}>
      <StartSidebar />
      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
        `}
      </style>
      <div className="p-6">
        <div className="max-w-4xl mx-auto pt-8">
        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            General Information
          </h1>
          <p className={`text-lg mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Please provide your information to get started
          </p>
          
        </div>

        {/* Main Form */}
        <div className={`shadow-lg rounded-lg overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 text-center">
            <h2 className="text-xl font-semibold text-white">
              {sections[currentSection].title}
            </h2>
          </div>
          
          <div className="p-6">
            {renderCurrentSection()}

            {/* Navigation Buttons */}
            <div className={`flex justify-between items-center mt-8 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={prevSection}
                disabled={currentSection === 0}
                className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg transition-all duration-200 ${
                  currentSection === 0
                    ? darkMode 
                      ? 'text-gray-500 bg-gray-700 border-gray-600 cursor-not-allowed'
                      : 'text-gray-400 bg-gray-100 border-gray-300 cursor-not-allowed'
                    : darkMode
                      ? 'text-gray-200 bg-gray-800 border-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                <FaArrowLeft className="mr-2" />
                Previous
              </button>

              {currentSection === sections.length - 1 ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={progress < 80}
                  className={`inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm transition-all duration-200 ${
                    progress >= 80
                      ? 'text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transform hover:scale-105'
                      : darkMode
                        ? 'text-gray-500 bg-gray-700 cursor-not-allowed'
                        : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                  }`}
                >
                  <FaArrowRight className="mr-2" />
                  Complete Discovery
                </button>
              ) : (
                <button
                  type="button"
                  onClick={nextSection}
                  disabled={
                    (currentSection === 0 && (
                      !formData.firstName || !formData.lastName || !formData.companyName || 
                      !formData.phoneNumber || !formData.timezone || !formData.streetAddress || 
                      !formData.city || !formData.state || !formData.zipCode
                    )) ||
                    (currentSection === 1 && (
                      !formData.businessStructure || 
                      (formData.businessStructure === 'Other' && !formData.businessStructureOther) ||
                      !formData.yearsInBusiness
                    ))
                  }
                  className={`inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm transition-all duration-200 ${
                    (currentSection === 0 && (
                      !formData.firstName || !formData.lastName || !formData.companyName || 
                      !formData.phoneNumber || !formData.timezone || !formData.streetAddress || 
                      !formData.city || !formData.state || !formData.zipCode
                    )) ||
                    (currentSection === 1 && (
                      !formData.businessStructure || 
                      (formData.businessStructure === 'Other' && !formData.businessStructureOther) ||
                      !formData.yearsInBusiness
                    ))
                      ? darkMode
                        ? 'text-gray-500 bg-gray-700 cursor-not-allowed'
                        : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                      : 'text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transform hover:scale-105'
                  }`}
                >
                  Next
                  <FaArrowRight className="ml-2" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Discovery;
